import type { TOwnerScope } from './useOwnerScope';
import type { TStudyRelevance } from './useStudyRelevance';

import { Stack } from 'common/ui/Layout';
import { OwnerFilterChipDropdown } from './OwnerFilterChipDropdown/OwnerFilterChipDropdown';
import { StudyFilterChipDropdown } from './StudyFilterChipDropdown/StudyFilterChipDropdown';
import SearchIcon from '@mui/icons-material/Search';
import Input from 'common/ui/Input';
import { RIGHT_DRAWER_FILTER_HEIGHT } from './constants';
import type { RightDrawerTabType } from './types';
import { Colors } from 'styles';
import styled from 'styled-components';
import { useFocusMode } from 'hooks/useFocusMode';
import { telemetryContextFor } from 'modules/Apollo/types';
import { memo } from 'react';

type SearchBarProps = {
  disabled: boolean;
};

const SearchBar = styled(Input).attrs(({ ...props }) => ({
  disabled: props.disabled,
  ...props,
}))`
  padding: 1px 6px;
  height: unset;
  background-color: ${(props: SearchBarProps) => (props.disabled ? Colors.gray5 : Colors.gray1)};
  color: ${Colors.gray10};
  border-color: ${Colors.gray8};
  cursor: ${(props: SearchBarProps) => (props.disabled ? 'not-allowed' : 'auto')};

  svg {
    height: 15px;
    width: 15px;
    color: ${Colors.gray10};
  }
`;

type RightDrawerFiltersProps = {
  search: string;
  setSearch: (arg1: string) => void;
  studyRelevance: TStudyRelevance;
  setStudyRelevance: (arg1: TStudyRelevance) => void;
  ownerScope: TOwnerScope;
  setOwnerScope: (arg1: TOwnerScope) => void;
  activeTab: RightDrawerTabType;
};

export const RightDrawerFilters = memo(
  ({
    search,
    setSearch,
    studyRelevance,
    setStudyRelevance,
    ownerScope,
    setOwnerScope,
    activeTab,
  }: RightDrawerFiltersProps): React.ReactElement => {
    const { isFocusModeOn } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });
    return (
      <Stack vertical css={{ margin: '8px 0px', height: RIGHT_DRAWER_FILTER_HEIGHT }}>
        <SearchBar
          type="search"
          data-testid={`${activeTab}-search`}
          icon={<SearchIcon />}
          placeholder="Search"
          value={search}
          onChange={(evt) => setSearch(evt.target.value)}
        />
        <Stack space="xsmall" css={{ marginTop: 8 }}>
          <StudyFilterChipDropdown selected={studyRelevance} onSelect={setStudyRelevance} />
          <OwnerFilterChipDropdown selected={ownerScope} onSelect={setOwnerScope} />
        </Stack>
      </Stack>
    );
  }
);

RightDrawerFilters.displayName = 'RightDrawerFilters';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RightDrawerFilters);
